export interface OfferHistoryItem {
    id: number
    offerId: number
    dateTime: string
    userId?: string | null
    userName?: string | null
    source: number | null
    action: number | null
    changesDetails: string | null
    validPeriodsHistory: OfferValidPeriodHistoryItem[]
    hasPayload: boolean
}

export interface OfferValidPeriodHistoryItem {
    id: number
    offerValidPeriodId: number
    operation: number
    periodStart: string
    periodEnd: string | null
    changesDetails: string | null
}

export interface OfferFieldChange {
    fieldName: string
    before: unknown
    after: unknown
}

export interface ParsedOfferChanges {
    changes: OfferFieldChange[]
    isBaseline: boolean
    warnings: string[]
    error?: string
}

// The JSON string in ChangesDetails was serialized independently of API camel casing.
export const parseOfferChanges = (json: string | null): ParsedOfferChanges => {
    const empty = { changes: [], isBaseline: false, warnings: [] }
    if (!json) return empty
    try {
        const details = JSON.parse(json)
        const changes = details.Changes || details.changes
        if (!Array.isArray(changes)) throw new Error('Invalid change details')
        const legacy = details.LegacyConversion || details.legacyConversion
        const warnings = legacy && (legacy.Warnings || legacy.warnings)
        return {
            changes: changes.map(change => {
                const fieldName = change.FieldName || change.fieldName
                if (typeof fieldName !== 'string') throw new Error('Invalid field name')
                return {
                    fieldName,
                    before: Object.prototype.hasOwnProperty.call(change, 'Before') ? change.Before : change.before,
                    after: Object.prototype.hasOwnProperty.call(change, 'After') ? change.After : change.after,
                }
            }),
            isBaseline: !!(legacy && (legacy.IsBaseline || legacy.isBaseline)),
            warnings: Array.isArray(warnings) ? warnings.filter(warning => typeof warning === 'string') : [],
        }
    } catch {
        return { ...empty, error: 'Change details could not be read. View the full payload for this entry.' }
    }
}

export const historySourceLabel = (source: number | null) => {
    const labels = ['supplier.traveldesk', 'b2b.traveldesk', 'b2b.traveldesk (Admin)', 'Auto import']
    return source == null ? 'Unknown source' : labels[source] || `Source ${source}`
}

export const historyActionLabel = (action: number | null) => {
    const labels = ['Added', 'Updated', 'Deleted', 'Restored', 'Published', 'Unpublished']
    return action == null ? 'Legacy history' : labels[action] || `Action ${action}`
}

export const historyFieldLabel = (name: string) => name
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/_/g, ' ')
    .replace(/\b(Usd|Aed|Id|Vat)\b/g, word => word.toUpperCase())

export const historyValueLabel = (value: unknown) => {
    if (value == null) return 'Not set'
    if (typeof value === 'boolean') return value ? 'Yes' : 'No'
    if (value === '') return '""'
    return typeof value === 'object' ? JSON.stringify(value, null, 2) : String(value)
}
